// PR-AJ Layer B — Shared court-availability helper.
//
// Used by every booking-creation path to give a clean Swedish error message
// BEFORE attempting INSERT. The DB trigger trg_bookings_no_overlap (Layer A)
// is the structural guarantee — this helper is purely UX so users see a
// clear toast instead of a Postgres error.
//
// Catches:
//   - Overlap with existing bookings (status confirmed or blocked, not soft-deleted)
//   - Overlap with active recurring_blocks pattern
//   - Overlap with placed league_matches (TASK-75 PR-D Layer 2; mirrors
//     check_booking_no_overlap trigger Section 3 added in migration
//     20260505134420_task_75_pr_d_check_booking_no_league_match_overlap.sql)
//
// Usage:
//   const result = await validateCourtAvailability(supabase, {
//     courtId, startTime, endTime, excludeBookingId
//   });
//   if (!result.available) {
//     toast.error(result.message);
//     return;
//   }

import type { SupabaseClient } from "@supabase/supabase-js";

export type AvailabilityCheckParams = {
  courtId: string;
  startTime: Date;
  endTime: Date;
  /**
   * Booking ID to exclude from overlap check (for reschedule flows where the
   * booking being moved should not block itself).
   */
  excludeBookingId?: string;
};

export type AvailabilityResult =
  | { available: true }
  | {
      available: false;
      reason: "overlapping_booking" | "blocked" | "recurring_block" | "league_match";
      /** Swedish UI-ready error message. */
      message: string;
      /** Conflicting booking ID / recurring block ID / league_match ID for diagnostics. */
      conflictId?: string;
    };

/**
 * Day-of-week in PostgreSQL `EXTRACT(dow FROM ...)` convention:
 * 0 = Sunday, 1 = Monday, ..., 6 = Saturday.
 * JavaScript `Date.getDay()` uses the same convention — just be aware to
 * convert from local-tz when the booking spans local timezone (we use
 * Europe/Stockholm by convention, matching the DB trigger).
 */
function localDateInStockholm(d: Date): { dateStr: string; dayOfWeek: number } {
  // Format the date in Europe/Stockholm timezone via Intl.
  // Using `sv-SE` locale yields YYYY-MM-DD format directly.
  const fmt = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  });
  const parts = fmt.formatToParts(d);
  const year = parts.find((p) => p.type === "year")!.value;
  const month = parts.find((p) => p.type === "month")!.value;
  const day = parts.find((p) => p.type === "day")!.value;
  // dayOfWeek via reconstruction (Date in UTC of YYYY-MM-DD parsed → getUTCDay)
  const dateStr = `${year}-${month}-${day}`;
  const dayOfWeek = new Date(`${dateStr}T00:00:00Z`).getUTCDay();
  return { dateStr, dayOfWeek };
}

/**
 * Check if a court is available for the requested time slot.
 *
 * Mirrors the DB trigger `trg_bookings_no_overlap` (Layer A) but runs
 * client-side for a clean UX before INSERT. Returns typed result with
 * Swedish error message ready for toast display.
 */
export async function validateCourtAvailability(
  supabase: SupabaseClient,
  params: AvailabilityCheckParams,
): Promise<AvailabilityResult> {
  const { courtId, startTime, endTime, excludeBookingId } = params;

  // ─── 1. Overlap with existing bookings (confirmed or blocked) ──────
  let bookingQuery = supabase
    .from("bookings")
    .select("id, status")
    .eq("court_id", courtId)
    .in("status", ["confirmed", "blocked"])
    .is("deleted_at", null)
    .lt("start_time", endTime.toISOString())
    .gt("end_time", startTime.toISOString())
    .limit(1);

  if (excludeBookingId) {
    bookingQuery = bookingQuery.neq("id", excludeBookingId);
  }

  const { data: overlapping, error: bookingErr } = await bookingQuery;

  if (bookingErr) {
    // Fail-open with a generic error so we don't block legitimate bookings
    // because of a Supabase outage. The DB trigger will catch real overlap.
    console.error("[validateCourtAvailability] booking query failed:", bookingErr);
    return { available: true };
  }

  if (overlapping && overlapping.length > 0) {
    const row = overlapping[0];
    const isBlocked = row.status === "blocked";
    return {
      available: false,
      reason: isBlocked ? "blocked" : "overlapping_booking",
      message: isBlocked
        ? "Banan är blockerad denna tid"
        : "Banan är redan bokad denna tid",
      conflictId: row.id,
    };
  }

  // ─── 2. Overlap with recurring_blocks pattern ─────────────────────
  // Fetch all active recurring blocks that touch this court + the booking's
  // local day-of-week + date range. Pattern math (interval_weeks +
  // skipped_dates) and time-of-day overlap are evaluated client-side because
  // recurring_blocks.start_time/end_time are 'time without time zone' values
  // in hall-local time, which doesn't translate cleanly to a single SQL filter.
  const { dateStr, dayOfWeek } = localDateInStockholm(startTime);

  const { data: recurringRows, error: rbErr } = await supabase
    .from("recurring_blocks")
    .select("id, court_ids, day_of_week, start_time, end_time, valid_from, valid_until, interval_weeks, skipped_dates")
    .contains("court_ids", [courtId])
    .eq("status", "active")
    .eq("day_of_week", dayOfWeek)
    .lte("valid_from", dateStr);

  if (rbErr) {
    console.error("[validateCourtAvailability] recurring_blocks query failed:", rbErr);
    return { available: true };
  }

  for (const rb of recurringRows ?? []) {
    // Validity window
    if (rb.valid_until && rb.valid_until < dateStr) continue;
    // Skipped specific dates
    if (rb.skipped_dates?.includes(dateStr)) continue;
    // Interval-weeks gating
    if (rb.interval_weeks && rb.interval_weeks > 1 && rb.valid_from) {
      const diffMs = new Date(dateStr).getTime() - new Date(rb.valid_from).getTime();
      const diffWeeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
      if (diffWeeks % rb.interval_weeks !== 0) continue;
    }

    // Time-of-day overlap. rb.start_time/end_time are local "HH:MM:SS" strings
    // in hall TZ. Compose the local datetime and parse as Stockholm time.
    // We use a textual comparison via ISO strings with Stockholm offset.
    // Since we're checking IF these overlap, and the booking is in absolute
    // time, the simplest correct comparison is: compute the block's instance
    // for `dateStr` in Stockholm tz, convert to UTC milliseconds, compare.
    const blockStartLocal = `${dateStr}T${rb.start_time}`;
    const blockEndLocal = `${dateStr}T${rb.end_time}`;
    // Heuristic: we know Stockholm tz offset from `Date` math by formatting.
    // Cleaner: just check if booking [start, end) intersects block in *local
    // walltime* representation, which is what the DB trigger does too.
    // The booking's local walltime via Intl:
    const bookingLocalStart = formatLocalISO(startTime);
    const bookingLocalEnd = formatLocalISO(endTime);
    if (bookingLocalStart < blockEndLocal && bookingLocalEnd > blockStartLocal) {
      return {
        available: false,
        reason: "recurring_block",
        message: "Banan är blockerad enligt återkommande mönster denna tid",
        conflictId: rb.id,
      };
    }
  }

  // ─── 3. Overlap with placed league_matches (TASK-75 PR-D Layer 2) ──
  // A league_match with `court_id` + `scheduled_date` + `scheduled_time`
  // set (and `status != 'cancelled'`) reserves that slot. We compose the
  // match's [start, end) window in Stockholm-local walltime — same shape
  // as the booking's via `formatLocalISO` — and check for overlap.
  // Mirrors `check_booking_no_overlap` trigger Section 3 (Layer 4).
  const { data: leagueMatches, error: lmErr } = await supabase
    .from("league_matches")
    .select("id, scheduled_time, leagues(match_duration_minutes)")
    .eq("court_id", courtId)
    .eq("scheduled_date", dateStr)
    .not("scheduled_time", "is", null)
    .neq("status", "cancelled");

  if (lmErr) {
    console.error("[validateCourtAvailability] league_matches query failed:", lmErr);
    return { available: true };
  }

  const bookingLocalStart = formatLocalISO(startTime);
  const bookingLocalEnd = formatLocalISO(endTime);

  for (const lm of (leagueMatches ?? []) as Array<{
    id: string;
    scheduled_time: string | null;
    leagues: { match_duration_minutes: number | null } | null;
  }>) {
    if (!lm.scheduled_time) continue;
    const durationMinutes = lm.leagues?.match_duration_minutes ?? 90;
    const matchStartLocal = `${dateStr}T${normalizeTimeString(lm.scheduled_time)}`;
    const matchEndLocal = `${dateStr}T${addMinutesToTimeString(lm.scheduled_time, durationMinutes)}`;

    if (bookingLocalStart < matchEndLocal && bookingLocalEnd > matchStartLocal) {
      return {
        available: false,
        reason: "league_match",
        message: "Tiden är reserverad för seriespel",
        conflictId: lm.id,
      };
    }
  }

  return { available: true };
}

/**
 * Normalize a Postgres `time` value (e.g. "17:30:00") to a fixed-width
 * "HH:MM:SS" string suitable for lexicographic comparison against
 * other `${YYYY-MM-DD}T${HH:MM:SS}` walltime strings.
 */
function normalizeTimeString(t: string): string {
  const parts = t.split(":");
  const h = String(Number(parts[0]) || 0).padStart(2, "0");
  const m = String(Number(parts[1]) || 0).padStart(2, "0");
  const s = String(Number(parts[2]) || 0).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

/**
 * Add `minutes` to an "HH:MM" or "HH:MM:SS" time string and return as
 * "HH:MM:SS". Used to compute a league_match's end walltime from its
 * start + match_duration_minutes (which lives on the leagues table).
 */
function addMinutesToTimeString(time: string, minutes: number): string {
  const parts = time.split(":");
  const h = Number(parts[0]) || 0;
  const m = Number(parts[1]) || 0;
  const s = Number(parts[2]) || 0;
  const totalMin = h * 60 + m + minutes;
  const newH = Math.floor(totalMin / 60);
  const newM = totalMin % 60;
  return `${String(newH).padStart(2, "0")}:${String(newM).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Format a Date as "YYYY-MM-DDTHH:MM:SS" in Europe/Stockholm wall-time.
 * Used for string-comparison against recurring_blocks.start_time/end_time
 * which are stored as local time-without-tz.
 */
function formatLocalISO(d: Date): string {
  const fmt = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}`;
}
